import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import calculateVatRate from 'bl/payment/calculateVatRate.bl';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { DOWNLOAD_EXCEL } from 'constants/csv.const';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCurrency } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import { exportCSV } from 'utils/converters/csv.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import currencySign from 'utils/intl/currencySign.util';
import linkTo from 'utils/links/linkTo.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';
import C from 'utils/values/constant.util';
import fixed from 'utils/values/fixed.util';

import CN from './FutureRevenueProjectionsTable.module.scss';

const TOOLTIP_OCCUPANCY = [
  'Please note, stylists with the following job titles will be excluded from the total occupancy calculation:',
  ' Apprentice, Graduate, Assistant, Front of house, Reception',
].join(S.space);

const TOOLTIP_APPOINTMENT = [
  'Number of appointments in the diary for this date range (excluding any cancelled bookings or no-shows).',
  'Appointments with 2 services count as 1. The same client with 2 separate appointments counts as 2.',
].join(S.space);

const TOOLTIP_REVENUE = [
  'Value of total appointments in the period, where value is the sum of service prices for these appointments.',
  'This figure is NOT based on the value checked out so excludes any discounts/retail - for this figure please see Revenue tab',
].join(S.space);

const TOOLTIP_AVR_BOOKING = [
  'Total booking value divided by Total number of appointments.',
  'This is different to Average Bill Value which is based on checked out appointments (see Revenue tab)',
].join(S.space);

const TOOLTIP_N_A = [
  'If you are seeing this, ',
  'it means that this person had appointments booked over a time period where they were not working/had time off',
].join(S.space);

const tooltipContents = C([
  { id: 1, text: TOOLTIP_OCCUPANCY, label: 'Future Occupancy' },
  { id: 2, text: TOOLTIP_APPOINTMENT, label: 'Future Appointments' },
  { id: 3, text: TOOLTIP_REVENUE, label: 'Future Revenue' },
  { id: 4, text: TOOLTIP_AVR_BOOKING, label: 'Future Avg Booking Value' },
]);

const NOT_APPLICABLE = 'N/A';

// NOTE: calculate total %
// taking all busy minutes (totalBusyMinutes) and / by all actual minutes ( totalActualMinutes) and * by 100

const calculateTotalOccupancyPercentage = (options) => {
  const stylistsGroupAppointments = options?.stylistsGroupAppointments || {};
  const stylistsGroupHours = options?.stylistsGroupHours || {};

  let totalBusyMinutes = 0;
  let totalActualMinutes = 0;

  for (const stylistId of Object.keys(stylistsGroupAppointments)) {
    const hours = stylistsGroupHours[stylistId];
    const busyMinutes = hours ? hours.busy_mins : 0;
    const actualMinutes = hours ? hours.actual_mins : 0;

    totalBusyMinutes += busyMinutes;
    totalActualMinutes += actualMinutes;
  }

  return 0 === totalActualMinutes ? 0 : (totalBusyMinutes / totalActualMinutes) * 100;
};

const FutureRevenueProjectionsTable = ({
  appointmentsReport,
  salonHoursReport,
  startDate,
  endDate,
  salonVatRate,
  stylistIDInCalendarOrder,
  currentSign,
}) => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();

  const marketingLink = linkTo({ pattern: LINK.marketing, params: { salonId }, fault: '#' });

  const stylistGroupAppointments = appointmentsReport.lastRequestResponse.stylist_group || {};
  const stylistGroupHours = salonHoursReport.lastRequestResponse.stylist_group || {};

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>Staff</th>
      {tooltipContents.map(({ text, label }, index) => {
        const isLast = index === tooltipContents.length - 1;
        return (
          <th key={createNodeKey({ index })}>
            <div className={CN.tableHeaderText}>
              <div className={CN.labelSpace}>{label}</div>
              <Tooltip content={text} variant={isLast ? 'quaternaryLeftToBottom' : 'quaternaryBottom'} width="max">
                <div className={CN.iconTooltip}>
                  <Icon variant="info" width="sm" />
                </div>
              </Tooltip>
            </div>
          </th>
        );
      })}
    </tr>
  );

  const tableRowsData = stylistIDInCalendarOrder
    .filter((stylist) => stylistGroupAppointments[stylist.id] && stylistGroupHours[stylist.id])
    .map((stylist) => {
      const stylistId = stylist.id;
      const appointment = stylistGroupAppointments[stylistId];
      const hours = stylistGroupHours[stylistId];
      const stylistName = appointment.name;
      const apointmentCount = appointment.count;

      const busyMinutes = hours ? hours.busy_mins : 0;
      const actualMinutes = hours ? hours.actual_mins : 0;

      // Calculate occupancy percentage with proper checks
      let occupancyPercentage = 0;
      if (actualMinutes !== 0 && !isNaN(busyMinutes) && !isNaN(actualMinutes)) {
        occupancyPercentage = ((busyMinutes / actualMinutes) * 100).toFixed();
      }

      const averageBookingValue = fixed(appointment.total / appointment.count);
      // calculate value after vat
      const totalAfterVat = calculateVatRate(appointment.total, salonVatRate);
      const averageBookingValueAfterVat = calculateVatRate(averageBookingValue, salonVatRate);

      return {
        stylistId,
        stylistName,
        apointmentCount,
        actualMinutes,
        occupancyPercentage,
        totalAfterVat,
        averageBookingValueAfterVat,
      };
    });

  // reorder stylist like in calendar
  const tableRows = asArray(tableRowsData).map((row) => {
    const {
      stylistId,
      stylistName,
      apointmentCount,
      actualMinutes,
      occupancyPercentage,
      totalAfterVat,
      averageBookingValueAfterVat,
    } = row;

    const occupancyTooltip =
      actualMinutes === 0 || !isNaN(actualMinutes) ? (
        <div className={CN.flex}>
          <Tooltip content={TOOLTIP_N_A} variant="quaternaryBottom" width="max">
            <div className={CN.flex}>
              <div className={CN.space}>{NOT_APPLICABLE} </div>
              <Icon variant="info" width="sm" />
            </div>
          </Tooltip>
        </div>
      ) : (
        `${occupancyPercentage}%`
      );

    return (
      <tr key={stylistId}>
        <td className={CN.firstCell}>{truncateString({ value: stylistName, max: 24, suffix: '...' })}</td>
        <td>{occupancyTooltip}</td>
        <td>{apointmentCount}</td>
        <td>
          <CurrencySign value={totalAfterVat} />
        </td>
        <td>
          <CurrencySign value={averageBookingValueAfterVat} />
        </td>
      </tr>
    );
  });

  const totalOccupancyPercentage = calculateTotalOccupancyPercentage({
    stylistsGroupAppointments: stylistGroupAppointments,
    stylistsGroupHours: stylistGroupHours,
  });
  // Filter the stylists to be displayed
  const visibleStylists = stylistIDInCalendarOrder.filter(
    (stylist) => stylistGroupAppointments[stylist.id] && stylistGroupHours[stylist.id],
  );

  // Calculating the total count for visible stylists only
  const salonTotalCount = visibleStylists.reduce(
    (total, stylist) => total + stylistGroupAppointments[stylist.id].count,
    0,
  );

  const salonTotalValue = Object.values(stylistGroupAppointments).reduce((total, stylist) => total + stylist.total, 0);

  const salonAverageBookingValue = salonTotalCount ? fixed(salonTotalValue / salonTotalCount) : 0;
  // calculate value after vat in footer
  const salonTotalValueAfterVat = calculateVatRate(salonTotalValue, salonVatRate);
  const salonAverageBookingValueAfterVat = calculateVatRate(salonAverageBookingValue, salonVatRate);

  const handleExportCsv = useCallback(() => {
    const headers = ['Staff', ...tooltipContents.map((tooltip) => tooltip.label)];

    const csvData = asArray(tableRowsData).map((item) => {
      const futureOccupancy =
        item.actualMinutes === 0 || !isNaN(item.actualMinutes) ? NOT_APPLICABLE : `${fixed(item.occupancyPercentage)}%`;

      const values = [
        item.stylistName,
        futureOccupancy,
        item.apointmentCount,
        currencySign({ value: item.totalAfterVat, currentSalonAbbr: currentSign }),
        currencySign({ value: item.averageBookingValueAfterVat, currentSalonAbbr: currentSign }),
      ];

      return Object.fromEntries(headers.map((header, i) => [header, values[i]]));
    });

    const footerValues = [
      'Salon Total',
      `${fixed(totalOccupancyPercentage)}%`,
      salonTotalCount,
      currencySign({ value: salonTotalValueAfterVat, currentSalonAbbr: currentSign }),
      currencySign({ value: salonAverageBookingValueAfterVat, currentSalonAbbr: currentSign }),
    ];

    const footer = headers.reduce((acc, header, index) => {
      let value = footerValues[index];
      //if headers.length !== footerValues.length, fill missing or undefined values with "N/A"
      if (value === undefined || value === null) {
        value = 'N/A';
      }

      acc[header] = value;
      return acc;
    }, {});

    if (footer) {
      csvData.push(footer);
    }

    exportCSV({ data: csvData, fileName: 'Future Revenue Projections' });
  }, [
    currentSign,
    salonAverageBookingValueAfterVat,
    salonTotalCount,
    salonTotalValueAfterVat,
    tableRowsData,
    totalOccupancyPercentage,
  ]);

  const hasTableData = fromAnyToBool(tableRowsData.length);

  return (
    <div
      data-bem="FutureRevenueProjectionsTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>Future Revenue Projections</div>
        <div className={CN.buttonsContainer}>
          <NavLink to={marketingLink}>
            <Button variant="primaryForce" width="small" onClick={alwaysNoop}>
              <span className={CN.buttonFont}>GET MORE BOOKINGS!</span>
            </Button>
          </NavLink>
          <Button onClick={handleExportCsv} disabled={!hasTableData} variant="primaryForce" width="small">
            <span className={CN.buttonFont}>{DOWNLOAD_EXCEL}</span>
          </Button>
        </div>
      </div>
      <div className={CN.timeContainer}>
        <div>
          From <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>to</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="FutureRevenueProjectionsTable">
          <thead>{tableHeader}</thead>
          <tbody>{tableRows}</tbody>
          <tfoot>
            <tr>
              <td className={CN.firstCell}>
                <span className={CN.bold}>Salon Total</span>
              </td>

              <td>
                <span className={CN.bold}>{fixed(totalOccupancyPercentage)}%</span>
              </td>
              <td>
                <span className={CN.bold}>{salonTotalCount}</span>
              </td>
              <td>
                <span className={CN.bold}>
                  <CurrencySign value={salonTotalValueAfterVat} />
                </span>
              </td>
              <td>
                <span className={CN.bold}>
                  <CurrencySign value={salonAverageBookingValueAfterVat} />
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointmentsReport: state?.reports?.appointmentsReport,
  salonHoursReport: state?.reports?.salonHoursReport,
  salonVatRate: state?.reports?.salonVatRate,
  stylistIDInCalendarOrder: state?.reports?.stylistIDInCalendarOrder,
  currentSign: selectCurrentSalonCurrency(state),
});

const StaffPerformanceTableHoc = connect(mapStateToProps)(FutureRevenueProjectionsTable);

export default StaffPerformanceTableHoc;
